// The TSIA2 highlighter.
//
// Behaviour from the reference screenshots: the toolbar button opens a white
// menu with `Stop Highlight` above four colours, the active row carries a
// check, and while a colour is active the pen icon's tip is tinted that
// colour. Drag-selecting or double-clicking inside the passage or the question
// stem paints the selection; each painted span carries a small red circle-x
// that removes it.
//
// Two rules that are easy to miss and both come from the real test: the colour
// stays active across questions until Stop Highlight is chosen, and only the
// passage and the question stem can be highlighted -- not the instructions and
// not the answer choices.
//
// STORAGE. A highlight is a pair of flat character offsets into the target's
// text, never saved markup, using the same `global/passageHighlight` helpers
// every other player uses. That is what lets the review regenerate the overlay
// against freshly rendered KaTeX instead of trusting HTML captured months
// earlier, and those helpers already handle the hard part -- a KaTeX construct
// is atomic, so a highlight either covers the whole of one or none of it.
import React, { useEffect, useRef, useState } from 'react';

import {
    generatePassageHTML,
    measurePassageSelectionOffsets,
} from 'global/passageHighlight';

export type HighlightColor = 'yellow' | 'green' | 'blue' | 'pink';

/** What the server stores and returns: offsets plus the target. */
export type TSIA2Annotation = {
    // null means the question stem rather than a passage.
    passageId: string | null;
    startIndex: number;
    endIndex: number;
    color: HighlightColor;
};

export const HIGHLIGHT_COLORS: { id: HighlightColor; label: string; swatch: string }[] = [
    { id: 'yellow', label: 'Yellow', swatch: '#fdf07a' },
    { id: 'green', label: 'Green', swatch: '#b8f2b1' },
    { id: 'blue', label: 'Blue', swatch: '#a9d8f5' },
    { id: 'pink', label: 'Pink', swatch: '#f9b6d2' },
];

// The pen icon, tinted at the tip by the active colour so the toolbar shows
// what a drag would paint without opening the menu.
export function HighlighterGlyph({ active }: { active: HighlightColor | null }) {
    const tint = HIGHLIGHT_COLORS.find(colour => colour.id === active)?.swatch ?? 'transparent';
    return (
        <span className='tsia2-highlighter-glyph' aria-hidden='true'>
            ✎
            <span className='tsia2-highlighter-tip' style={{ background: tint }} />
        </span>
    );
}

export function HighlighterMenu({
    active, onChoose, onStop, onDismiss,
}: {
    active: HighlightColor | null;
    onChoose: (colour: HighlightColor) => void;
    onStop: () => void;
    onDismiss: () => void;
}) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocumentClick = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) onDismiss();
        };
        // Deferred so the click that opened the menu does not immediately close it.
        const id = window.setTimeout(
            () => document.addEventListener('mousedown', onDocumentClick), 0,
        );
        return () => {
            window.clearTimeout(id);
            document.removeEventListener('mousedown', onDocumentClick);
        };
    }, [onDismiss]);

    return (
        <div className='tsia2-highlighter-menu' ref={ref} role='menu'>
            <button type='button' className='tsia2-highlighter-item' onClick={onStop} role='menuitem'>
                <span className='tsia2-highlighter-stop' aria-hidden='true'>⊗</span>
                Stop Highlight
                {active === null && <span className='tsia2-highlighter-check'>✓</span>}
            </button>
            {HIGHLIGHT_COLORS.map(colour => (
                <button
                    key={colour.id}
                    type='button'
                    className='tsia2-highlighter-item'
                    onClick={() => onChoose(colour.id)}
                    role='menuitem'
                >
                    <span className='tsia2-highlighter-swatch' style={{ background: colour.swatch }} />
                    {colour.label}
                    {active === colour.id && <span className='tsia2-highlighter-check'>✓</span>}
                </button>
            ))}
        </div>
    );
}

/** Measure the live selection against one highlightable element.
 *
 * Returns null when the selection is empty, or escapes the element -- the
 * caller tries each target in turn, so a selection that starts in the passage
 * and ends in the stem simply paints nothing, which is what the real test does
 * with a selection that straddles structure.
 */
export function measureAgainst(
    element: Element | null,
    passageId: string | null,
    colour: HighlightColor,
): TSIA2Annotation | null {
    if (!element) return null;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return null;
    const range = measurePassageSelectionOffsets(element, selection);
    if (!range) return null;
    return {
        passageId,
        startIndex: range.startIndex,
        endIndex: range.endIndex,
        color: colour,
    };
}

/** Overlay markup for one target's highlights, in the order they were made.
 *
 * Each highlight becomes its own absolutely-positioned copy of the text with
 * only its own span marked, stacked over the real text -- the arrangement the
 * SHSAT review uses, which is what lets several colours overlap without one
 * rewriting another's markup.
 */
export function overlayFor(element: Element | null, annotations: TSIA2Annotation[]) {
    if (!element) return [];
    return annotations.map((annotation, index) => ({
        key: `${annotation.startIndex}-${annotation.endIndex}-${index}`,
        color: annotation.color,
        html: generatePassageHTML(element, annotation.startIndex, annotation.endIndex).passageHTML,
        annotation,
    }));
}


/** Read-only highlight layers, for the review.
 *
 * Same arrangement the player uses and the same one the SHSAT review uses:
 * one absolutely-positioned copy of the text per highlight, with only that
 * highlight's span marked, stacked over the real text. Regenerated from the
 * stored offsets against the freshly rendered markup, which is why a saved
 * highlight still lands correctly months later.
 */
export function HighlightLayers({
    target, annotations, layout,
}: {
    target: React.RefObject<HTMLElement>;
    annotations: TSIA2Annotation[];
    layout: number;
}) {
    const [overlays, setOverlays] = useState<ReturnType<typeof overlayFor>>([]);

    useEffect(() => {
        setOverlays(overlayFor(target.current, annotations));
    }, [target, annotations, layout]);

    return (
        <>
            {overlays.map(overlay => (
                <div
                    key={overlay.key}
                    className={`tsia2-overlay tsia2-overlay-${overlay.color}`}
                    aria-hidden='true'
                    dangerouslySetInnerHTML={{ __html: overlay.html }}
                />
            ))}
        </>
    );
}
